import {
  AppstoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  ExpandAltOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  RobotOutlined,
  SendOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Flex,
  Image,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  message as toast,
  Typography, // Alias to avoid conflict with state
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../UserContext';
import ChatBubble from '../../components/ChatBubble';
import { Agent, AgentMessage, BaseComponentProps } from '../../types';
import {
  formatDateFromUTCtoAgent,
  formatDateHeader,
  getAgentTimezone,
  getCrmLink,
  renderContent,
} from './utils';

const { Title, Paragraph, Text } = Typography;

export const Conversation = ({
  agent,
  apiUrl,
  clientConfig,
}: BaseComponentProps): JSX.Element => {
  const user = useContext(UserContext);
  const [messages, setMessages] = useState<AgentMessage[] | undefined>(
    undefined,
  );
  const [selectedMessage, setSelectedMessage] = useState<AgentMessage | null>(
    null,
  );
  const limit = 20;
  const [offset, setOffset] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const [aiTransformLoading, setAiTransformLoading] = useState<boolean>(false);
  const listRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useRef<boolean>(false);
  const linkedMessage = useRef({ done: false, attempts: 0, prevCount: 0 });
  const [hashKey, setHashKey] = useState(window.location.hash);
  const [messageImages, setMessageImages] = useState<Record<string, any[]>>({});
  const agentTimezone = getAgentTimezone(agent, clientConfig);

  // Move metaEntries logic outside of JSX
  const shownKeys = [
    'reminders',
    'leads',
    'extra_instructions',
    'block_time_description',
  ];
  const metaEntries =
    selectedMessage && selectedMessage.meta != null
      ? Object.entries(selectedMessage.meta).filter(
          ([key, value]) =>
            !shownKeys.includes(key) &&
            value !== undefined &&
            value !== null &&
            value !== '',
        )
      : null;

  const fetchMessages = async (
    agent: Agent,
    limit: number,
    offset: number,
    reset: boolean,
  ) => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/messaging/agent/${agent.id}?limit=${limit}&offset=${offset}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });
      const jsonData = await response.json();

      if (reset) {
        setOffset(jsonData.length);
        setMessages(jsonData);
      } else {
        setMessages(prev =>
          [...jsonData, ...(prev ?? [])].sort(
            (a, b) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime(),
          ),
        );
        setOffset(prev => prev + limit);
      }
    } catch (err) {
      console.error('An error occurred while fetching the messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageImages = async (messageId: string) => {
    try {
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/messaging/message/${messageId}/images?include_recent=true`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch images for message ${messageId}`);
        return;
      }

      const imageData = await response.json();
      const message = messages?.find(m => m.id === messageId);
      const filteredImages = message
        ? imageData.filter(
            (img: { created_at: string }) =>
              new Date(img.created_at).getTime() <=
              new Date(message.created_at).getTime(),
          )
        : imageData;

      setMessageImages(prev => ({
        ...prev,
        [messageId]: filteredImages,
      }));
    } catch (err) {
      console.error(`Error fetching images for message ${messageId}:`, err);
    }
  };

  const openModal = (message: AgentMessage) => {
    setSelectedMessage(message);
    void fetchMessageImages(message.id);
  };

  const closeModal = () => {
    setSelectedMessage(null);
  };

  const resetLinkedMessage = () => {
    linkedMessage.current = { done: false, attempts: 0, prevCount: 0 };
  };

  const highlightMessage = (el: HTMLElement) => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.transition = 'background-color 0.3s ease';
    el.style.backgroundColor = '#e6f4ff';
    setTimeout(() => { el.style.backgroundColor = ''; }, 2000);
  };

  useEffect(() => {
    if (!window.location.hash.startsWith('#message-')) {
      scrollToBottom.current = true;
    }
    resetLinkedMessage();
    fetchMessages(agent, limit, 0, true).catch(console.error);
  }, [agent]);

  useEffect(() => {
    const onHashChange = () => { resetLinkedMessage(); setHashKey(window.location.hash); };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Scroll to hash-linked message, auto-loading older pages if needed
  useEffect(() => {
    const lm = linkedMessage.current;
    if (loading || lm.done || !messages?.length) return;
    if (!window.location.hash.startsWith('#message-')) return;

    const el = document.getElementById(window.location.hash.slice(1));
    if (el) { lm.done = true; setOffset(messages.length); highlightMessage(el); return; }

    const exhausted = lm.attempts > 0 && messages.length === lm.prevCount;
    if (exhausted || lm.attempts >= 5) {
      lm.done = true;
      setOffset(messages.length);
      toast.warning('Message not found — it may have been deleted or is too old.');
      return;
    }

    if (lm.attempts === 0) toast.info('Searching for linked message...');
    lm.prevCount = messages.length;
    lm.attempts += 1;
    fetchMessages(agent, limit, messages.length, false).catch(console.error);
  }, [loading, messages, hashKey]);

  useEffect(() => {
    if (scrollToBottom.current) {
      const timeout = setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight + 1;
          scrollToBottom.current = false;
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [messages, offset]);

  const transformToAiVoice = async () => {
    try {
      setAiTransformLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      if (!message.trim()) {
        console.error('No message to transform');
        return;
      }

      const url = `${apiUrl}/api/calls/ai-voice-transform`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          agentId: agent.id,
          message: message,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to transform message: ${await response.text()}`,
        );
      }

      const data = await response.json();
      setMessage(data.transformed_message);
    } catch (err) {
      console.error('An error occurred while transforming the message:', err);
    } finally {
      setAiTransformLoading(false);
    }
  };

  const send = async () => {
    try {
      setSendLoading(true);
      if (!user?.accessToken) {
        console.error('No access token available');
        return;
      }

      const url = `${apiUrl}/api/calls/sms`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          agentId: agent.id,
          message: message,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to send message ${await response.text()}`);
      }

      setMessage('');
      await fetchMessages(agent, 1, 0, false);
    } catch (err) {
      console.error('An error occurred while sending the message:', err);
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <div>
      <Spin spinning={loading}>
        <Flex
          ref={listRef}
          style={{ height: '70vh', overflowY: 'auto', padding: 12 }}
          vertical
        >
          <Button
            onClick={() => void fetchMessages(agent, limit, offset, false)}
          >
            Load More
          </Button>

          {messages?.map((message, index) => {
            // Calculate if we need a date header
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const currentDateHeader = formatDateHeader(
              message.created_at,
              agentTimezone,
            );
            const prevDateHeader = prevMessage
              ? formatDateHeader(prevMessage.created_at, agentTimezone)
              : null;
            const showDateHeader = currentDateHeader !== prevDateHeader;

            return (
              <div key={message.id || index} id={`message-${message.id}`} style={{ marginBottom: 8 }}>
                {/* Date Separator */}
                {showDateHeader && (
                  <Divider
                    plain
                    style={{
                      margin: '16px 0',
                      color: '#999',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {currentDateHeader}
                  </Divider>
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: message.incoming
                      ? 'flex-start'
                      : 'flex-end',
                    flexDirection: 'column',
                  }}
                >
                  {/* Optional Extra Info (Sender · Time) */}
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#bbb', // Softer color for less distraction
                      marginBottom: 2,
                      alignSelf: message.incoming ? 'flex-start' : 'flex-end',
                      textAlign: message.incoming ? 'left' : 'right',
                      paddingLeft: message.incoming ? 4 : 0,
                      paddingRight: message.incoming ? 0 : 4,
                    }}
                  >
                    {message.incoming ? 'Agent' : 'You'} ·{' '}
                    {formatDateFromUTCtoAgent(
                      message.created_at,
                      agentTimezone,
                    )}
                  </div>

                  <div
                    className={`chat-bubble-container ${
                      message.incoming ? 'incoming' : 'outgoing'
                    }`}
                  >
                    <ChatBubble
                      isOwnMessage={!message.incoming}
                      message={message.message}
                      timestamp={formatDateFromUTCtoAgent(
                        message.created_at,
                        agentTimezone,
                      )}
                    />
                    <div className="chat-actions">
                      <Button
                        icon={<CopyOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          if (typeof message.message === 'string') {
                            void navigator.clipboard.writeText(message.message);
                            toast.success('Copied!');
                          }
                        }}
                        shape="circle"
                        size="small"
                        title="Copy message"
                        type="text"
                      />
                      <Button
                        icon={<ExpandAltOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          openModal(message);
                        }}
                        shape="circle"
                        size="small"
                        title="Open details"
                        type="text"
                      />
                      <Button
                        icon={<LinkOutlined />}
                        onClick={e => {
                          e.stopPropagation();
                          const url = `${window.location.origin}${window.location.pathname}${window.location.search}#message-${message.id}`;
                          void navigator.clipboard.writeText(url);
                          toast.success('Link copied!');
                        }}
                        shape="circle"
                        size="small"
                        title="Copy link to message"
                        type="text"
                      />
                    </div>
                  </div>

                  {/* Optional ID or metadata snippet */}
                  {message.meta?.status && (
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#999',
                        marginTop: 4,
                        alignSelf: message.incoming ? 'flex-start' : 'flex-end',
                      }}
                    >
                      Status: {message.meta.status}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Flex>

        <Flex gap="small" justify="flex-end" style={{ marginTop: 12 }}>
          <Input.TextArea
            onChange={e => setMessage(e.target.value)}
            placeholder="Type your message..."
            rows={3}
            style={{ flex: 0.8 }}
            value={message}
          />
          <Button
            disabled={!message.trim()}
            icon={<RobotOutlined />}
            loading={aiTransformLoading}
            onClick={() => void transformToAiVoice()}
            style={{ height: 'fit-content' }}
            title="Transform to AI Voice"
          />
          <Popconfirm
            cancelText="No"
            description="Are you sure you want to send to agent?"
            okText="Send"
            onConfirm={() => void send()}
            title="Send text"
          >
            <Button
              disabled={!message.trim()}
              icon={<SendOutlined />}
              loading={sendLoading}
              style={{ height: 'fit-content' }}
              type="primary"
            />
          </Popconfirm>
        </Flex>
      </Spin>

      {/* Modal for Selected Message */}
      <Modal
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 16 }}
        footer={null}
        onCancel={closeModal}
        open={!!selectedMessage}
        title={`📩 Message Details - ${selectedMessage?.id}`}
        width={700}
      >
        {selectedMessage && (
          <div style={{ paddingRight: 8 }}>
            <Title level={4} style={{ marginBottom: 12 }}>
              Message Details
            </Title>

            {/* Message Content */}
            <Card
              size="small"
              style={{ marginBottom: 16 }}
              title={<Text strong>📩 Message</Text>}
            >
              {selectedMessage.message}
            </Card>

            {/* Timestamp and Incoming */}
            <Card bordered={false} size="small" style={{ marginBottom: 16 }}>
              <Text strong>
                <ClockCircleOutlined
                  style={{ marginRight: 8, color: '#1890ff' }}
                />
                Timestamp:
              </Text>{' '}
              <Text>
                {formatDateFromUTCtoAgent(
                  selectedMessage.created_at,
                  agentTimezone,
                )}
              </Text>
              <Divider type="vertical" />
              <Text strong>
                <ExclamationCircleOutlined
                  style={{
                    marginRight: 8,
                    color: selectedMessage.incoming ? '#52c41a' : '#f5222d',
                  }}
                />
                {selectedMessage.incoming ? 'Incoming' : 'Outgoing'}
              </Text>
            </Card>

            {/* Phoenix Traces */}
            {(selectedMessage.trace_ids?.length ||
              selectedMessage.trace_id) && (
              <Card
                bordered={false}
                size="small"
                style={{ marginBottom: 16 }}
                title={<Text strong>Phoenix Traces</Text>}
              >
                {selectedMessage.trace_ids?.length ? (
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: '100%' }}
                  >
                    {selectedMessage.trace_ids.map((info, index) => (
                      <div
                        key={info.trace_id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <a
                          href={`${process.env.REACT_APP_PHOENIX_TRACE_URL_PREFIX}/${info.trace_id}`}
                          rel="noopener noreferrer"
                          style={{ color: '#1890ff' }}
                          target="_blank"
                        >
                          Trace {index + 1}
                        </a>
                        <Text type="secondary">
                          {formatDateFromUTCtoAgent(
                            info.timestamp,
                            agentTimezone,
                          )}
                        </Text>
                      </div>
                    ))}
                  </Space>
                ) : selectedMessage.trace_id ? (
                  // Backwards compatibility for old messages
                  <a
                    href={`${process.env.REACT_APP_PHOENIX_TRACE_URL_PREFIX}/${selectedMessage.trace_id}`}
                    rel="noopener noreferrer"
                    style={{ color: '#1890ff' }}
                    target="_blank"
                  >
                    {selectedMessage.trace_id}
                  </a>
                ) : null}
              </Card>
            )}

            {/* Reminders */}
            {selectedMessage.meta?.reminders &&
              selectedMessage.meta.reminders.length > 0 && (
                <Card
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <span>
                      <CalendarOutlined
                        style={{ marginRight: 6, color: '#fa8c16' }}
                      />
                      Reminders
                    </span>
                  }
                >
                  {selectedMessage.meta.reminders.map((reminder: any) => (
                    <Card
                      key={reminder.id}
                      size="small"
                      style={{
                        marginBottom: 12,
                        borderLeft: '4px solid #fa8c16',
                      }}
                      type="inner"
                    >
                      <Text strong>{reminder.description || 'Reminder'}</Text>
                      <div style={{ marginTop: 6 }}>
                        <TagOutlined style={{ marginRight: 4 }} />
                        <Text style={{ marginRight: 12 }} type="secondary">
                          Type: {reminder.meta?.type || 'N/A'}
                        </Text>
                        <Text style={{ marginRight: 12 }} type="secondary">
                          Variant: {reminder.meta?.variant || 'N/A'}
                        </Text>
                        <Text type="secondary">
                          Message Type: {reminder.meta?.message_type || 'N/A'}
                        </Text>
                      </div>
                      <div
                        style={{
                          marginTop: 6,
                          fontStyle: 'italic',
                          color: '#888',
                        }}
                      >
                        {reminder.hardcoded_message}
                      </div>
                      <div
                        style={{ marginTop: 8, fontSize: 13, color: '#555' }}
                      >
                        <ClockCircleOutlined style={{ marginRight: 6 }} />
                        {new Date(reminder.datetime).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </div>
                    </Card>
                  ))}
                </Card>
              )}

            {/* Leads */}
            {selectedMessage.meta?.leads &&
              selectedMessage.meta.leads.length > 0 && (
                <Card
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <span>
                      <UserOutlined
                        style={{ marginRight: 6, color: '#722ed1' }}
                      />
                      Leads
                    </span>
                  }
                >
                  {selectedMessage.meta.leads.map((lead: any) => (
                    <Card
                      key={lead.id}
                      size="small"
                      style={{
                        marginBottom: 12,
                        borderLeft: '4px solid #722ed1',
                      }}
                      type="inner"
                    >
                      <Title level={5} style={{ marginBottom: 4 }}>
                        {lead.first_name} {lead.last_name}
                      </Title>
                      <Paragraph>
                        <MailOutlined />{' '}
                        <a href={`mailto:${lead.email}`}>{lead.email}</a>
                        <br />
                        <PhoneOutlined /> {lead.phone}
                        <br />
                        <a
                          href={
                            lead.crm_link ||
                            getCrmLink(clientConfig, lead.source_id)
                          }
                          rel="noopener noreferrer"
                          style={{ color: '#722ed1' }}
                          target="_blank"
                        >
                          CRM Link
                        </a>
                      </Paragraph>
                    </Card>
                  ))}
                </Card>
              )}

            {/* Images */}
            {messageImages[selectedMessage.id] &&
              messageImages[selectedMessage.id].length > 0 && (
                <Card
                  size="small"
                  style={{ marginBottom: 16 }}
                  title={
                    <span>
                      <img
                        alt="Images Icon"
                        src="https://img.icons8.com/color/24/000000/image-gallery.png"
                        style={{ marginRight: 6 }}
                      />
                      Images
                    </span>
                  }
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {messageImages[selectedMessage.id].map((img, idx) => (
                      <div
                        key={idx}
                        style={{
                          border: img.is_recent
                            ? '2px dashed #1890ff'
                            : '1px solid #ccc',
                          borderRadius: 6,
                          padding: 4,
                          maxWidth: 120,
                        }}
                      >
                        <Image
                          alt={`Image ${idx + 1}${img.is_recent ? ' (recent)' : ''}`}
                          preview
                          src={img.image_url}
                          style={{ borderRadius: 4 }}
                          width={110}
                        />
                        {img.is_recent && (
                          <Text
                            style={{
                              fontSize: 11,
                              textAlign: 'center',
                              display: 'block',
                              marginTop: 4,
                            }}
                            type="success"
                          >
                            Recent
                          </Text>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

            {/* Extra instructions */}
            {selectedMessage.meta?.extra_instructions && (
              <Card
                size="small"
                style={{ marginBottom: 16, whiteSpace: 'pre-wrap' }}
                title={<Text strong>Extra Instructions</Text>}
              >
                {selectedMessage.meta.extra_instructions}
              </Card>
            )}

            {/* Block time description */}
            {selectedMessage.meta?.block_time_description?.description && (
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  whiteSpace: 'pre-wrap',
                  backgroundColor: '#fafafa',
                }}
                title={<Text strong>Block Time Description</Text>}
              >
                {selectedMessage.meta.block_time_description.description}
              </Card>
            )}

            {/* Metadata (other meta fields) */}
            {metaEntries && metaEntries.length > 0 && (
              <Collapse style={{ marginBottom: 16 }}>
                <Collapse.Panel
                  header={
                    <span>
                      <AppstoreOutlined
                        style={{ marginRight: 6, color: '#1890ff' }}
                      />
                      Metadata
                    </span>
                  }
                  key="meta"
                >
                  <Descriptions bordered column={1} size="small">
                    {metaEntries.map(([key, value]) => (
                      <Descriptions.Item key={key} label={key}>
                        {renderContent(value)}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </Collapse.Panel>
              </Collapse>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
